/**
 * Style System Showcase
 * Demonstrates the new style system components and patterns
 */

import React, { useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  TextField,
  Typography,
  Alert,
  Chip,
  IconButton,
  Paper,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Checkbox,
  FormControlLabel,
  Grid,
  Divider,
} from '@mui/material';
import {
  Close,
  Add,
  Edit,
  Delete,
  Save,
  Cancel,
  HelpOutline,
  Settings,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { 
  usePanelStyles,
  useCardStyles,
  useButtonStyles,
  useFormFieldStyles,
  useAlertStyles,
} from '../utilities/hooks';
import DensitySelector from '../components/DensitySelector';

export default function StyleShowcase() {
  const theme = useTheme();
  const [selectedCard, setSelectedCard] = useState(null);
  const [textValue, setTextValue] = useState('');
  const [selectValue, setSelectValue] = useState('');
  const [checked, setChecked] = useState(false);
  
  // Use style hooks
  const panelStyles = usePanelStyles();
  const cardStyles = useCardStyles({ clickable: true });
  const selectedCardStyles = useCardStyles({ clickable: true, selected: true });
  const buttonStyles = useButtonStyles();
  const formFieldStyles = useFormFieldStyles();
  const alertStyles = useAlertStyles();
  
  return (
    <Box sx={{ 
      height: '100%',
      overflow: 'auto',
      '&::-webkit-scrollbar': {
        width: 8,
        height: 8,
      },
      '&::-webkit-scrollbar-thumb': {
        backgroundColor: 'rgba(0, 0, 0, 0.2)',
        borderRadius: 4,
        '&:hover': {
          backgroundColor: 'rgba(0, 0, 0, 0.3)',
        },
      },
    }}>
      <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Typography variant="h4">
          Style System Showcase
        </Typography>
        <Chip 
          label={`Current Density: ${theme.density || 'comfortable'} (${theme.densityScale || 1.0}x)`}
          color="primary"
          sx={{ fontSize: theme.typography.fontSize?.sm }}
        />
      </Box>
      
      {/* Density Settings */}
      <Paper sx={{ ...panelStyles.root, mb: 3 }}>
        <Box sx={{ ...panelStyles.header, backgroundColor: theme.palette.primary.main, color: 'white' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Settings sx={{ fontSize: 20 }} />
            <Typography sx={{ ...panelStyles.headerTitle, color: 'white' }}>
              Display Density Settings
            </Typography>
          </Box>
        </Box>
        <Box sx={panelStyles.content}>
          <DensitySelector />
        </Box>
      </Paper>
      
      <Divider sx={{ my: 4 }} />
      
      {/* Color Palette */}
      <Paper sx={panelStyles.root}>
        <Box sx={panelStyles.header}>
          <Typography sx={panelStyles.headerTitle}>Color Palette</Typography>
        </Box>
        <Box sx={panelStyles.content}>
          <Grid container spacing={2}>
            {['primary', 'secondary', 'error', 'warning', 'info', 'success'].map((color) => (
              <Grid item xs={6} sm={4} md={2} key={color}>
                <Box
                  sx={{
                    bgcolor: `${color}.main`,
                    color: `${color}.contrastText`,
                    p: 2,
                    borderRadius: 1,
                    textAlign: 'center',
                    textTransform: 'capitalize',
                  }}
                >
                  {color}
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>
      </Paper>
      
      {/* Typography */}
      <Paper sx={{ ...panelStyles.root, mt: 3 }}>
        <Box sx={panelStyles.header}>
          <Typography sx={panelStyles.headerTitle}>Typography Scale</Typography>
        </Box>
        <Box sx={panelStyles.content}>
          <Typography sx={{ fontSize: theme.typography.fontSize['2xl'], mb: 1 }}>
            2xl - Main Headers ({theme.typography.fontSize['2xl']})
          </Typography>
          <Typography sx={{ fontSize: theme.typography.fontSize.xl, mb: 1 }}>
            xl - Page Titles ({theme.typography.fontSize.xl})
          </Typography>
          <Typography sx={{ fontSize: theme.typography.fontSize.lg, mb: 1 }}>
            lg - Headers ({theme.typography.fontSize.lg})
          </Typography>
          <Typography sx={{ fontSize: theme.typography.fontSize.md, mb: 1 }}>
            md - Emphasis Text ({theme.typography.fontSize.md})
          </Typography>
          <Typography sx={{ fontSize: theme.typography.fontSize.base, mb: 1 }}>
            base - Standard Text ({theme.typography.fontSize.base})
          </Typography>
          <Typography sx={{ fontSize: theme.typography.fontSize.sm, mb: 1 }}>
            sm - Helper Text ({theme.typography.fontSize.sm})
          </Typography>
          <Typography sx={{ fontSize: theme.typography.fontSize.xs }}>
            xs - Tiny Labels ({theme.typography.fontSize.xs})
          </Typography>
        </Box>
      </Paper>
      
      {/* Buttons */}
      <Paper sx={{ ...panelStyles.root, mt: 3 }}>
        <Box sx={panelStyles.header}>
          <Typography sx={panelStyles.headerTitle}>
            Button Variants (Height: {theme.dimensions?.button?.medium || 32}px)
          </Typography>
        </Box>
        <Box sx={panelStyles.content}>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
            <Button 
              variant="contained" 
              size="small"
              sx={{ 
                height: theme.dimensions?.button?.small,
                fontSize: theme.typography.fontSize?.sm 
              }}
            >
              Small ({theme.dimensions?.button?.small}px)
            </Button>
            <Button 
              variant="contained"
              sx={{ 
                height: theme.dimensions?.button?.medium,
                fontSize: theme.typography.fontSize?.sm 
              }}
            >
              Medium ({theme.dimensions?.button?.medium}px)
            </Button>
            <Button 
              variant="contained"
              sx={{ 
                height: theme.dimensions?.button?.large,
                fontSize: theme.typography.fontSize?.base 
              }}
            >
              Large ({theme.dimensions?.button?.large}px)
            </Button>
          </Box>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
            <Button variant="outlined" size="small">Outlined</Button>
            <Button variant="text" size="small">Text</Button>
            <Button variant="contained" color="secondary" size="small">Secondary</Button>
            <Button variant="contained" color="success" size="small">Success</Button>
            <Button variant="contained" color="warning" size="small">Warning</Button>
            <Button variant="contained" color="error" size="small">Error</Button>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <IconButton size="small">
              <Add sx={{ fontSize: theme.dimensions?.icon?.small }} />
            </IconButton>
            <IconButton size="small">
              <Edit sx={{ fontSize: theme.dimensions?.icon?.small }} />
            </IconButton>
            <IconButton size="small">
              <Delete sx={{ fontSize: theme.dimensions?.icon?.small }} />
            </IconButton>
            <IconButton size="small">
              <Save sx={{ fontSize: theme.dimensions?.icon?.small }} />
            </IconButton>
            <IconButton size="small">
              <Cancel sx={{ fontSize: theme.dimensions?.icon?.small }} />
            </IconButton>
            <IconButton size="small">
              <HelpOutline sx={{ fontSize: theme.dimensions?.icon?.small }} />
            </IconButton>
          </Box>
        </Box>
      </Paper>
      
      {/* Form Fields */}
      <Paper sx={{ ...panelStyles.root, mt: 3 }}>
        <Box sx={panelStyles.header}>
          <Typography sx={panelStyles.headerTitle}>
            Form Fields (Input height: {theme.dimensions?.input?.medium || 32}px)
          </Typography>
        </Box>
        <Box sx={panelStyles.content}>
          <Box sx={formFieldStyles.field}>
            <Typography sx={formFieldStyles.label}>Text Field</Typography>
            <TextField
              size="small"
              fullWidth
              placeholder="Enter text..."
              value={textValue}
              onChange={(e) => setTextValue(e.target.value)}
              sx={{
                ...formFieldStyles.input,
                '& .MuiInputBase-input': {
                  fontSize: theme.typography.fontSize?.base,
                  height: `${(theme.dimensions?.input?.medium || 32) - 16}px`,
                }
              }}
            />
          </Box>
          
          <Box sx={formFieldStyles.field}>
            <Typography sx={formFieldStyles.label}>Select Field</Typography>
            <FormControl size="small" fullWidth>
              <Select
                value={selectValue}
                onChange={(e) => setSelectValue(e.target.value)}
                displayEmpty
              >
                <MenuItem value="">Choose an option...</MenuItem>
                <MenuItem value="1">Option 1</MenuItem>
                <MenuItem value="2">Option 2</MenuItem>
                <MenuItem value="3">Option 3</MenuItem>
              </Select>
            </FormControl>
          </Box>
          
          <Box sx={formFieldStyles.field}>
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={checked}
                  onChange={(e) => setChecked(e.target.checked)}
                />
              }
              label="Checkbox Option"
            />
          </Box>
        </Box>
      </Paper>
      
      {/* Cards */}
      <Paper sx={{ ...panelStyles.root, mt: 3 }}>
        <Box sx={panelStyles.header}>
          <Typography sx={panelStyles.headerTitle}>Card Components</Typography>
        </Box>
        <Box sx={panelStyles.content}>
          <Grid container spacing={2}>
            {[1, 2, 3].map((id) => (
              <Grid item xs={12} sm={6} md={4} key={id}>
                <Card
                  sx={selectedCard === id ? selectedCardStyles.root : cardStyles.root}
                  onClick={() => setSelectedCard(id)}
                >
                  <CardContent sx={cardStyles.content}>
                    <Typography sx={cardStyles.title}>
                      Card Title {id}
                    </Typography>
                    <Typography sx={cardStyles.subtitle}>
                      Subtitle or metadata
                    </Typography>
                    <Typography sx={cardStyles.body}>
                      This is the card body content. Click to select this card.
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      </Paper>
      
      {/* Alerts */}
      <Paper sx={{ ...panelStyles.root, mt: 3 }}>
        <Box sx={panelStyles.header}>
          <Typography sx={panelStyles.headerTitle}>Alert Variants</Typography>
        </Box>
        <Box sx={panelStyles.content}>
          <Alert severity="info" sx={alertStyles.root}>
            This is an info alert with standard styling
          </Alert>
          <Alert severity="success" sx={alertStyles.root}>
            This is a success alert
          </Alert>
          <Alert severity="warning" sx={alertStyles.root}>
            This is a warning alert
          </Alert>
          <Alert severity="error" sx={alertStyles.root}>
            This is an error alert
          </Alert>
          <Alert severity="info" sx={alertStyles.compact}>
            This is a compact alert variant
          </Alert>
        </Box>
      </Paper>
      
      {/* Chips */}
      <Paper sx={{ ...panelStyles.root, mt: 3 }}>
        <Box sx={panelStyles.header}>
          <Typography sx={panelStyles.headerTitle}>Chip Components</Typography>
        </Box>
        <Box sx={panelStyles.content}>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Chip label="Default" size="small" />
            <Chip label="Primary" color="primary" size="small" />
            <Chip label="Secondary" color="secondary" size="small" />
            <Chip label="Success" color="success" size="small" />
            <Chip label="Warning" color="warning" size="small" />
            <Chip label="Error" color="error" size="small" />
            <Chip label="Clickable" size="small" onClick={() => {}} />
            <Chip label="Deletable" size="small" onDelete={() => {}} />
          </Box>
        </Box>
      </Paper>
      
      {/* Spacing */}
      <Paper sx={{ ...panelStyles.root, mt: 3 }}>
        <Box sx={panelStyles.header}>
          <Typography sx={panelStyles.headerTitle}>
            Spacing Scale (Base unit: {theme.spacing(1)})
          </Typography>
        </Box>
        <Box sx={panelStyles.content}>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-end', flexWrap: 'wrap' }}>
            {[0.5, 1, 1.5, 2, 3, 4, 5, 6].map((spacing) => (
              <Box key={spacing} sx={{ textAlign: 'center' }}>
                <Box
                  sx={{
                    width: theme.spacing(spacing),
                    height: theme.spacing(spacing),
                    bgcolor: 'primary.main',
                    mb: 0.5,
                  }}
                />
                <Typography sx={{ fontSize: theme.typography.fontSize?.xs }}>
                  {spacing}
                </Typography>
                <Typography sx={{ fontSize: theme.typography.fontSize?.xs, color: 'text.secondary' }}>
                  {theme.spacing(spacing)}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </Paper>
      
      {/* Shadows */}
      <Paper sx={{ ...panelStyles.root, mt: 3, mb: 3 }}>
        <Box sx={panelStyles.header}>
          <Typography sx={panelStyles.headerTitle}>Shadow Depths</Typography>
        </Box>
        <Box sx={panelStyles.content}>
          <Grid container spacing={3}>
            {[0, 1, 3, 6, 9, 12].map((elevation) => (
              <Grid item xs={6} sm={4} md={2} key={elevation}>
                <Paper
                  elevation={elevation}
                  sx={{
                    p: 2,
                    textAlign: 'center',
                    height: 60,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Typography sx={{ fontSize: '0.75rem' }}>
                    Elevation {elevation}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Box>
      </Paper>
      </Box>
    </Box>
  );
}